'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  EditIcon,
  TrashIcon,
  FileTextIcon,
  HashIcon,
  LanguagesIcon,
  MapPinIcon,
  PhoneIcon,
  MailIcon,
  PrinterIcon,
  CopyIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { toast } from 'sonner'
import { formatDateTime, getLocalizedValue, getCountryCodeFromLocale } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { ContentDetailInfo, ContentError, DeleteConfirmationDialog } from '@/components/partials'
import { useContactUnit, useDeleteContactUnit, useCopyToClipboard } from '@/hooks'
import { useMetaStore } from '@/store'

type PropsType = {
  id: string
}

const ContactUnitDetailSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-6 w-32" />
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const locale = useLocale()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deleteContactUnitId, setContactUnitId] = useState<string | null>(null)

  const { data: contactUnit, isLoading, error } = useContactUnit(id)
  const { metaState } = useMetaStore()
  const locales = metaState.response?.locales
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const { mutate: deleteContactUnit, isPending: isDeleting } = useDeleteContactUnit()

  const hasTranslation = (localeCode: string): boolean => {
    return contactUnit?.translations?.[localeCode] !== undefined
  }

  const handleDelete = () => {
    setContactUnitId(id)
  }

  const confirmDeleteContactUnit = () => {
    if (deleteContactUnitId) {
      deleteContactUnit(deleteContactUnitId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['contact-units'] })
          toast.success(t('deleted_successfully'))
          router.push('/contact-units')
          setContactUnitId(null)
        },
        onError: error => {
          console.error('Error deleting contact unit:', error)
          toast.error(t('error_deleting'))
          setContactUnitId(null)
        }
      })
    }
  }

  if (isLoading || isDeleting) {
    return <ContactUnitDetailSkeleton />
  }

  if (error || !contactUnit) {
    return <ContentError error={error} listUrl="/contact-units" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/contact-units">
              <ArrowLeftIcon className="h-4 w-4" />
              <span className="hidden lg:flex">{t('list')}</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('contact_unit_details')}</h1>
            <p className="text-muted-foreground">{t('view_contact_unit_details')}</p>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button variant="outline" asChild>
            <Link href={`/contact-units/${id}/edit`}>
              <EditIcon className="h-4 w-4" />
              <span className="hidden lg:flex">{t('edit_contact_unit')}</span>
            </Link>
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <TrashIcon className="h-4 w-4" />
            <span className="hidden lg:flex">{t('delete')}</span>
          </Button>
        </div>
      </div>

      {/* Contact Unit Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileTextIcon className="h-5 w-5" />
            {t('information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <HashIcon className="h-4 w-4" />
                ID
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono text-xs">
                  {contactUnit.id}
                </Badge>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(contactUnit.id)}
                      className="h-6 w-6 p-0">
                      <CopyIcon className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('copy')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <FileTextIcon className="h-4 w-4" />
                {t('title')}
              </div>
              <p className="text-sm">{getLocalizedValue(contactUnit, 'title', locale, defaultLocale)}</p>
            </div>
          </div>
          <Separator />
          <ContentDetailInfo entity={contactUnit} />
        </CardContent>
      </Card>

      {/* Translations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LanguagesIcon className="h-5 w-5" />
            {t('translations')}
          </CardTitle>
          <CardDescription>{t('available_translations')}</CardDescription>
        </CardHeader>
        <CardContent>
          {locales && locales.length > 0 ? (
            <Tabs defaultValue={locales[0].fullCode} className="w-full">
              <TabsList className="min-h-10.5 w-full">
                {locales.map(localeItem => (
                  <TabsTrigger
                    key={localeItem.fullCode}
                    value={localeItem.fullCode}
                    className="flex items-center gap-2"
                    disabled={!hasTranslation(localeItem.fullCode)}>
                    <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={16} />
                    <span className="hidden sm:inline">{localeItem.name}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {locales.map(localeItem => (
                <TabsContent key={localeItem.fullCode} value={localeItem.fullCode} className="mt-6 space-y-4">
                  {hasTranslation(localeItem.fullCode) ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                        <h3 className="text-lg font-semibold">{localeItem.name}</h3>
                        <Badge variant="outline" className="text-xs">
                          {localeItem.fullCode}
                        </Badge>
                      </div>

                      <Separator />

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-muted-foreground text-sm font-medium">{t('title')}</label>
                          <div className="bg-muted/50 rounded-lg border p-4">
                            <p className="text-sm leading-relaxed">
                              {contactUnit.translations[localeItem.fullCode].title}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('created_at')}</label>
                            <p className="text-sm">
                              {formatDateTime(contactUnit.translations[localeItem.fullCode].createdAt, locale)}
                            </p>
                          </div>

                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('updated_at')}</label>
                            <p className="text-sm">
                              {formatDateTime(contactUnit.translations[localeItem.fullCode].updatedAt, locale)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <div className="text-muted-foreground flex items-center justify-center gap-2">
                        <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                        <span>{localeItem.name}</span>
                      </div>
                      <p className="text-muted-foreground mt-2">{t('no_translation_available')}</p>
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">{t('no_languages_available')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Location */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPinIcon className="h-5 w-5" />
            {t('location')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="text-muted-foreground text-sm font-medium">{t('address')}</div>
            <p className="text-sm">{contactUnit.address}</p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <div className="text-muted-foreground text-sm font-medium">{t('latitude')}</div>
              <Badge variant="outline">{contactUnit.latitude ?? '-'}</Badge>
            </div>
            <div className="space-y-2">
              <div className="text-muted-foreground text-sm font-medium">{t('longitude')}</div>
              <Badge variant="outline">{contactUnit.longitude ?? '-'}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      {contactUnit.phones?.length || contactUnit.faxes?.length || contactUnit.emails?.length ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PhoneIcon className="h-5 w-5" />
              {t('contact_information')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {contactUnit.phones && contactUnit.phones.length > 0 && (
              <div className="space-y-3">
                <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                  <PhoneIcon className="h-4 w-4" />
                  {t('phones')}
                </div>
                <div className="flex flex-wrap gap-2">
                  {contactUnit.phones.map((phone, index) => (
                    <Badge key={index} variant="secondary">
                      {phone}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {contactUnit.faxes && contactUnit.faxes.length > 0 && (
              <div className="space-y-3">
                <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                  <PrinterIcon className="h-4 w-4" />
                  {t('faxes')}
                </div>
                <div className="flex flex-wrap gap-2">
                  {contactUnit.faxes.map((fax, index) => (
                    <Badge key={index} variant="secondary">
                      {fax}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {contactUnit.emails && contactUnit.emails.length > 0 && (
              <div className="space-y-3">
                <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                  <MailIcon className="h-4 w-4" />
                  {t('emails')}
                </div>
                <div className="space-y-2">
                  {contactUnit.emails.map((email, index) => (
                    <div key={index} className="rounded-lg border p-3">
                      <div className="text-sm font-medium">{email.name}</div>
                      <a href={`mailto:${email.email}`} className="text-primary text-sm hover:underline">
                        {email.email}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={!!deleteContactUnitId}
        onCancel={() => setContactUnitId(null)}
        onConfirm={confirmDeleteContactUnit}
      />
    </div>
  )
}

export default Detail
